import { useDisclosure } from "@chakra-ui/react";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SortableTh from "../../components/Core/SortableTh/SortableTh";
import VirtualizedTableLayout from "../../components/Layout/VirtualizedTableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import { useListFilters } from "../../components/Shared/Filters/useListFilters";
import ColumnResizeHandle from "../../components/Shared/ListColumns/ColumnResizeHandle";
import { ListColumn } from "../../components/Shared/ListColumns/types";
import { useColumnResize } from "../../components/Shared/ListColumns/useColumnResize";
import { useListColumns } from "../../components/Shared/ListColumns/useListColumns";
import ListPageToolbar from "../../components/Shared/ListPageToolbar/ListPageToolbar";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import ListViewTagChips, {
	hasListViewTypes,
} from "../../components/Shared/TagChip/ListViewTagChips";
import { CustomerBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import Customer from "../../models/Customer";
import {
	CustomerFilterDefinition,
	EMPTY_CUSTOMER_DEFINITION,
	customerDefinitionsEqual,
	isEmptyCustomerDefinition,
	matchesCustomer,
} from "../../models/CustomerFilter";
import { SORT_ORDER } from "../../models/Sorting";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import { cn } from "../../utils/cn";
import { customerRoute } from "../../utils/route.utils";
import CustomerFilterPanel from "./components/CustomerFilterPanel";
import { useCustomerFilterData } from "./components/useCustomerFilterData";

/**
 * Customer list rendered with the virtualized table layout (the /customers
 * page).
 *
 *  - Loads the *entire* filtered set up-front (no server pagination) so the
 *    whole list lives in one virtualized scroll container.
 *  - Search and sort happen client-side over the in-memory list.
 *
 * Filtering is owned by the shared `useListFilters` hook and column visibility
 * by `useListColumns`; this page supplies only its declarative column set and the
 * data source. Mirrors ConsultantsListVirtualizedPage.
 */

const CustomersListVirtualizedPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const filters = useListFilters<CustomerFilterDefinition>({
		filtersKey: "customerFilters",
		definitionsEqual: customerDefinitionsEqual,
		isEmptyDefinition: isEmptyCustomerDefinition,
		emptyDefinition: EMPTY_CUSTOMER_DEFINITION,
	});
	const {
		pool,
		setPool,
		searchTerm,
		definition,
		setDefinition,
		savedFilters,
		orgFilters,
		selectedSavedId,
		handleSaveNew,
		handleUpdateSaved,
		handleRenameSaved,
		handleDeleteSaved,
		handleReorderSaved,
	} = filters;

	// Sort is keyed by column `key` (not an enum) so a column owns its own sort.
	const [sort, setSort] = useState<string | undefined>(undefined);
	const [order, setOrder] = useState<SORT_ORDER>(SORT_ORDER.DEFAULT);

	const filterPanel = useDisclosure();

	const keyword = useDebounce(searchTerm, 250);

	// React Query owns the full list per pool. Filter/search/sort are client-side
	// over the cached result. The same hook derives the panel's options and the
	// pool/contract name maps used to label those columns.
	const {
		customers: allCustomers,
		options: filterOptions,
		poolNameById,
		contractNameById,
		isLoading,
	} = useCustomerFilterData(pool);

	// Customer tag types opted in to list views (org settings) gate the "tags"
	// column: it only exists — and appears in the picker — when something is opted in.
	const { data: organisation } = useOrganisation();
	const customerTagTypes = organisation?.customerTagTypes;
	const tagsAvailable = hasListViewTypes(customerTagTypes);

	// The subsidiary column only exists (and appears in the picker) when the
	// organisation has subsidiaries.
	const { subsidiaries } = useSubsidiaries();
	const subsidiaryNameById = useMemo(
		() => new Map(subsidiaries.map((s) => [s.id, s.name])),
		[subsidiaries]
	);

	// The declarative column set. `name` is mandatory; pool and contract are opt-in
	// (default off). Weights are relative — a visible column's width is its weight
	// over the sum of visible weights.
	const columns = useMemo<ListColumn<Customer>[]>(
		() => [
			{
				key: "name",
				label: "NAME",
				weight: 24,
				mandatory: true,
				pinned: true,
				headerClassName: "pl-6",
				cellClassName: "pl-6",
				sortValue: (c) => c.name || "",
				renderCell: (c) => c.name,
			},
			{
				key: "customerNo",
				label: "CUSTOMER_NO_SHORT",
				weight: 12,
				sortValue: (c) => c.customerNumber || "",
				renderCell: (c) => c.customerNumber,
			},
			{
				key: "vat",
				label: "VAT_NO",
				weight: 14,
				sortValue: (c) => c.vatNumber || "",
				renderCell: (c) => c.vatNumber,
			},
			{
				key: "created",
				label: "CREATED",
				weight: 14,
				sortValue: (c) => (c.metadata?.created ? String(c.metadata.created) : ""),
				renderCell: (c) =>
					c.metadata?.created ? moment(c.metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE) : "",
			},
			{
				key: "pool",
				label: "POOL",
				weight: 12,
				defaultVisible: false,
				renderCell: (c) => (c.pool ? poolNameById.get(c.pool) : ""),
			},
			{
				key: "subsidiary",
				label: "organisation.subsidiaries.title",
				weight: 12,
				available: subsidiaryNameById.size > 0,
				defaultVisible: false,
				sortValue: (c) => (c.subsidiaryId && subsidiaryNameById.get(c.subsidiaryId)) || "",
				renderCell: (c) => (c.subsidiaryId ? subsidiaryNameById.get(c.subsidiaryId) : ""),
			},
			{
				key: "contract",
				label: "CONTRACT",
				weight: 14,
				defaultVisible: false,
				renderCell: (c) => (c.contract?.id ? contractNameById.get(c.contract.id) : ""),
			},
			{
				key: "tags",
				label: "Taggar",
				weight: 16,
				available: tagsAvailable,
				renderCell: (c) => <ListViewTagChips tags={c.tags} tagTypes={customerTagTypes} />,
			},
			{
				key: "signup",
				label: "customer.isSignupShort",
				weight: 8,
				renderCell: (c) => (c.isSignup ? t("YES") : ""),
			},
			{
				key: "status",
				label: "STATUS",
				weight: 12,
				cellClassName: (c) => (!c.active ? "text-danger" : undefined),
				renderCell: (c) => (c.active ? t("ACTIVE") : t("INACTIVE")),
			},
		],
		[t, poolNameById, contractNameById, tagsAvailable, customerTagTypes, subsidiaryNameById]
	);

	const listColumns = useListColumns<Customer>(
		"customerListColumns",
		columns,
		"customerListColumnWidths",
		"customerListColumnOrder"
	);
	const { visibleColumns, setColumnWidths, resetColumnWidth } = listColumns;

	const resize = useColumnResize<Customer>({
		columns: visibleColumns,
		onCommit: setColumnWidths,
		onReset: resetColumnWidth,
	});

	// If the sorted column gets hidden via the picker, drop the sort.
	useEffect(() => {
		if (sort && !visibleColumns.some((c) => c.key === sort)) {
			setSort(undefined);
			setOrder(SORT_ORDER.DEFAULT);
		}
	}, [visibleColumns, sort]);

	const handleSortColumn = (column: string) => {
		if (sort !== column) {
			setSort(column);
			setOrder(SORT_ORDER.ASC);
		} else {
			setOrder((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
		}
	};

	// Widths use the resize-aware weight so an in-progress drag previews live.
	const totalWeight = visibleColumns.reduce((sum, c) => sum + resize.weightOf(c.key), 0);
	const widthOf = (col: ListColumn<Customer>) =>
		`${(resize.weightOf(col.key) / totalWeight) * 100}%`;

	// Client-side search + sort over the in-memory list.
	const rows = useMemo(() => {
		const term = (keyword || "").trim().toLowerCase();

		let result = allCustomers.filter((c) => matchesCustomer(c, definition));

		if (term) {
			result = result.filter((c) => {
				const haystack = [c.name, c.customerNumber, c.vatNumber, c.phoneNumber]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				return haystack.includes(term);
			});
		}

		const activeColumn = visibleColumns.find((c) => c.key === sort);
		if (activeColumn?.sortValue && order !== SORT_ORDER.DEFAULT) {
			const valueOf = activeColumn.sortValue;
			result = [...result].sort((a, b) => {
				const cmp = valueOf(a).localeCompare(valueOf(b), "sv", { numeric: true });
				return order === SORT_ORDER.ASC ? cmp : -cmp;
			});
		}

		return result;
	}, [allCustomers, definition, keyword, sort, order, visibleColumns]);

	const navigateToCustomer = (id?: string) => navigate(customerRoute(id || ""));

	const renderTableHeader = useCallback(() => {
		return (
			<thead className="border-b-2 border-border">
				<tr className="text-left bg-muted text-sm font-medium whitespace-nowrap">
					{visibleColumns.map((col) => {
						const handle = resize.handleProps(col.key);
						return col.sortValue ? (
							<SortableTh
								key={col.key}
								label={t(col.label as any)}
								column={col.key}
								activeColumn={sort}
								order={order}
								onSort={handleSortColumn}
								width={widthOf(col)}
								className={col.headerClassName}
								resizeHandle={handle && <ColumnResizeHandle {...handle} />}
							/>
						) : (
							<th
								key={col.key}
								className={cn("relative pt-3 pb-2 px-4", col.headerClassName)}
								style={{ width: widthOf(col) }}
							>
								{t(col.label as any)}
								{handle && <ColumnResizeHandle {...handle} />}
							</th>
						);
					})}
				</tr>
			</thead>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleColumns, sort, order, t, resize]);

	const renderTableRow = useCallback(
		(index: number) => {
			const customer = rows[index];
			if (!customer) return null;

			const baseCellClass =
				"px-4 py-2 border-t border-border border-solid align-middle text-base truncate";

			return (
				<>
					{visibleColumns.map((col) => {
						const extra =
							typeof col.cellClassName === "function"
								? col.cellClassName(customer)
								: col.cellClassName;
						return (
							<td
								key={col.key}
								className={cn(baseCellClass, extra)}
								style={{ width: widthOf(col) }}
								onClick={col.stopClickPropagation ? (e) => e.stopPropagation() : undefined}
							>
								{col.renderCell(customer)}
							</td>
						);
					})}
				</>
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[rows, visibleColumns, resize]
	);

	return (
		<>
			<Breadcrumbs
				items={CustomerBreadcrumbs}
				showPoolDropdown
				onChangePool={setPool}
				selectedPool={pool}
			/>

			<ListPageToolbar
				filters={filters}
				count={rows.length}
				onOpenFilterPanel={filterPanel.onOpen}
				createTo={routes.CUSTOMER_CREATE}
				createLabel="CREATE_CUSTOMER"
				columns={listColumns}
				menuActions={[
					{
						label: "MANAGE_CORPORATE_GROUPS",
						onClick: () => navigate(routes.CUSTOMER_CORPORATE_GROUPS),
					},
					{
						label: "LIST_CUSTOMER_USERS",
						onClick: () => navigate(routes.CUSTOMER_USERS_LIST),
					},
				]}
			/>

			<CustomerFilterPanel
				isOpen={filterPanel.isOpen}
				onClose={filterPanel.onClose}
				definition={definition}
				onChange={setDefinition}
				options={filterOptions}
				savedFilters={savedFilters}
				orgFilters={orgFilters}
				activeSavedId={selectedSavedId}
				onSaveNew={handleSaveNew}
				onUpdateSaved={handleUpdateSaved}
				onRenameSaved={handleRenameSaved}
				onDeleteSaved={handleDeleteSaved}
				onReorderSaved={handleReorderSaved}
			/>

			{isLoading ? (
				<LoadingIndicator className="mt-4" />
			) : rows.length ? (
				<VirtualizedTableLayout
					itemCount={rows.length}
					estimatedRowHeight={48}
					overscan={10}
					renderHeader={renderTableHeader}
					renderRow={renderTableRow}
					getRowProps={(index) =>
						({ "data-customer": rows[index]?.id } as React.HTMLAttributes<HTMLTableRowElement>)
					}
					onRowClick={(index) => navigateToCustomer(rows[index]?.id)}
					containerHeight="calc(100vh - 100px)"
					selectable
				/>
			) : (
				<div className="px-4">
					<EmptyState margin="2rem 0" label="NO_CUSTOMERS" />
				</div>
			)}
		</>
	);
};

export default CustomersListVirtualizedPage;
